import {
  Device,
  MATCH_START,
  ROUTE_START,
  SUB_FLOW,
} from "callable-data-objects";
import Account from "callable-data-objects/dist/models/account/Account";
import {
  CLIENT,
  DevicePost,
  DevicePut,
  QUEUE,
} from "callable-data-objects/dist/models/device";
import { DeviceService } from "callable-data-objects/dist/services/Services";
import { useAccount } from "Hooks/Endpoints";
import { set } from "lodash";
import { APIAccountDeviceContext } from "Providers/APIContexts";
import { CreateDeviceProp, DefaultProviderProps } from "Providers/types";
import React, { useEffect, useState } from "react";

const APIDeviceProvider = ({ children }: DefaultProviderProps) => {
  const accountService = useAccount();
  const [deviceService, setDeviceService] = useState<DeviceService>(
    accountService.deviceService
  );
  const [account, setAccount] = useState<Account | undefined>();
  const [devices, setDevices] = useState<Device[] | undefined>();
  const [routeStarts, setRouteStarts] = useState<Device[] | undefined>();
  const [activeFlow, setActiveFlow] = useState<Device | undefined>();
  const [device, setDevice] = useState<Device | undefined>();
  const [createDevice, setCreateDevice] = useState<
    CreateDeviceProp | undefined
  >();
  const [nextExtension, setNextExtension] = useState<number | undefined>();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [activeMenu, setActiveMenu] = useState<string>(ROUTE_START);

  const isDeviceFlow = (device: Device): boolean => {
    return (
      device.type === ROUTE_START ||
      device.type === MATCH_START ||
      device.type === SUB_FLOW
    );
  };

  const latestExtension = (devices: Device[]): number => {
    if (devices.length > 0) {
      const ext = devices.reduce((p, c) =>
        parseInt(p.extension) > parseInt(c.extension) ? p : c
      ).extension;
      return parseInt(ext) + 1;
    }
    return 100;
  };

  const getActiveFlow = (): Device | undefined => {
    if (routeStarts && activeFlow) {
      const rs = routeStarts.find((d) => activeFlow.id === d.id);
      if (rs) return new Device(rs);
      return undefined;
    } else if (routeStarts && routeStarts.length > 0) {
      return new Device(routeStarts[0]);
    } else {
      return undefined;
    }
  };

  async function buildDevices() {
    const resp: Device[] = await deviceService.list();
    if (resp) {
      console.log(resp);
      const newDevices = resp.map((d) => new Device(d));
      const newFlows = newDevices.filter((d) => isDeviceFlow(d));
      setDevices(newDevices);
      setRouteStarts(newFlows);
      setNextExtension(latestExtension(resp));
    }
  }

  useEffect(() => {
    if (accountService) {
      const { account, deviceService } = accountService;
      setDeviceService(deviceService);
      setAccount(account);
    }
    if (deviceService && !routeStarts) {
      buildDevices();
    }

    if (!activeFlow && routeStarts) {
      setActiveFlow(getActiveFlow());
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountService, deviceService, routeStarts]);

  async function select(id: string): Promise<void> {
    if (deviceService) {
      const response = await deviceService.get(id);
      setDevice(new Device(response));
      setCreateDevice(undefined);
      setActiveMenu(response.type);
    }
  }

  function selectFlow(id: string): void {
    setActiveFlow(undefined);
    setDevice(undefined);
    setCreateDevice(undefined);
    if (deviceService) {
      deviceService.get(id).then((resp) => setActiveFlow(resp));
    }
  }

  function unselect(): void {
    setCreateDevice(undefined);
    setDevice(undefined);
  }

  async function update(payload: DevicePut) {
    setSubmitting(true);
    if (accountService && deviceService && account && device) {
      await deviceService.put(device.id, payload);
      await accountService.select(account.id);
      select(device.id);
      if (activeFlow) {
        if (deviceService) {
          const response = await deviceService.get(activeFlow.id);
          setActiveFlow(new Device(response));
          buildDevices();
        }
      }
    }
    setSubmitting(false);
  }

  async function save(payload: DevicePost) {
    console.log("save device");
    if (createDevice?.noStep) {
      return saveDevice(payload);
    }
    setSubmitting(true);
    if (deviceService && device && createDevice) {
      const response = await deviceService.post("", payload);
      if (response) {
        set(device, `${createDevice.type}.${createDevice.field}`, response.id);
        await update(device);
        select(device.id);
        buildDevices();
      }
    }
    setSubmitting(false);
  }

  async function saveFlow(payload: DevicePost) {
    setSubmitting(true);
    if (deviceService && createDevice) {
      const response = await deviceService.post("", payload);
      if (response) {
        select(response.id);
        buildDevices();
        selectFlow(response.id);
      }
    }
    setSubmitting(false);
  }

  async function saveDevice(payload: DevicePost) {
    setSubmitting(true);
    if (deviceService && createDevice) {
      try {
        const response = await deviceService.post("", payload);
        if (response) {
          select(response.id);
          buildDevices();
        }
      } catch (error) {
        alert("Sorry, we cannot save device");
      }
    }
    setSubmitting(false);
  }

  async function create(type: string, field: string) {
    setCreateDevice({ type, field });
  }

  async function createFlow() {
    setCreateDevice({ deviceType: "flow" });
  }

  async function createDeviceByType(type: string) {
    setCreateDevice({ deviceType: type, noStep: true });
  }

  async function createType(type: string) {
    setCreateDevice({ deviceType: type });
  }

  async function remove(id: string) {
    setSubmitting(true);
    try {
      await deviceService.delete(id);
    } catch (error) {
      alert("Cannot delete device. Please check no devices reference the device you are trying to delete");
    }
    setDevice(undefined);
    if (activeFlow && activeFlow.id === id) {
      setActiveFlow(undefined);
    }
    buildDevices();
    setSubmitting(false);
  }

  function validateName(value: string): string | undefined {
    let error: string | undefined;
    if (value === null || value === "") error = "Name required";
    if (value.length < 4) error = "Name must be 4 or more characters";
    if (devices) {
      if (device) {
        const found = devices
          .filter((d) => d.id !== device.id)
          .find((d) => d.name === value);
        if (found) error = "Name in use";
      } else {
        const found = devices.find((d) => d.name === value);
        if (found) error = "Name in use";
      }
    }
    return error;
  }

  function validateClientUsername(value: string): string | undefined {
    let error: string | undefined;
    if (value === null || value === "") error = "Username required";
    if (value.length < 4) error = "Username must be 4 or more characters";
    if (devices) {
      const found = devices
        .filter((d) => !device || d.id !== device.id)
        .filter((d) => d.type === CLIENT)
        .find((d) => d.client.username === value);
      if (found) error = "Username in use";
    }
    return error;
  }

  function validateQueueName(value: string): string | undefined {
    let error: string | undefined;
    if (value === null || value === "") error = "Queue Name required";
    if (value.length < 4) error = "Queue Name must be 4 or more characters";
    if (devices) {
      const found = devices
        .filter((d) => !device || d.id !== device.id)
        .filter((d) => d.type === QUEUE)
        .find((d) => d.queue.queueName === value);
      if (found) error = "Queue Name in use";
    }
    return error;
  }

  function validatePassword(value: string): string | undefined {
    let error: string | undefined;
    if (value === null || value === "") error = "Password required";
    let regex = new RegExp(
      "^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{12,})"
    );
    if (!value.match(regex)) {
      error =
        "Password Strength requires at least 12 chars, 1 Digit, 1 Lowercase, 1 Uppercase & 1 Special Character";
    }
    return error;
  }

  function validateExtension(value: string): string | undefined {
    let error: string | undefined;
    if (value === null || value === "") error = "Extension required";
    if (devices) {
      if (device) {
        const found = devices
          .filter((d) => d.id !== device.id)
          .find((d) => d.extension === value);
        if (found) error = "Extension in use";
      } else {
        const found = devices.find((d) => d.extension === value);
        if (found) error = "Extension in use";
      }
    }
    return error;
  }

  function uncreate() {
    setCreateDevice(undefined);
  }

  function onMenu(m: string) {
    setActiveMenu(m);
    uncreate();
    unselect();
  }

  return (
    <APIAccountDeviceContext.Provider
      value={{
        api: accountService.api,
        account,
        devices,
        routeStarts,
        activeFlow,
        device,
        createDevice,
        nextExtension,
        submitting,
        select,
        selectFlow,
        unselect,
        update,
        create,
        createFlow,
        createType,
        createDeviceByType,
        save,
        saveFlow,
        saveDevice,
        remove,
        validateName,
        validateExtension,
        validateClientUsername,
        validateQueueName,
        validatePassword,
        uncreate,
        activeMenu,
        onMenu,
      }}
    >
      {children}
    </APIAccountDeviceContext.Provider>
  );
};

export default APIDeviceProvider;
